import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { publish, subscribe } from 'shuttle';
import { authenticate, isFunction } from 'swipeclock-frontend';
import {
  setMasterData as setMasterDataForLocalUser,
  setUser,
} from 'store/userSlice';
import {
  setMasterData as setMasterDataForSwipeclockUser,
  setMetricsData,
} from 'store/swipeclockUserSlice';
import { getUrlParameter, isMobileMode } from 'helpers/core/url';
import BootstrapAuthContext from 'components/BootstrapAuthContext';

export const MOBILE_SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please close and reopen Time Clock.';
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired.';
export const AUTH_FAILED_MESSAGE =
  'Authentication failed. Please log in again.';
export const EXPIRED_JWT_MESSAGE = 'expired-jwt';

const isEnclosedMode = () => getUrlParameter('enclosed') === 'true';

function WebClockBootstrap({ children, name, invalidationFn }) {
  const dispatch = useDispatch();
  const [isMobile] = useState(isMobileMode);
  const [isEnclosed] = useState(isEnclosedMode);
  const [sessionNotice, setSessionNotice] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const hasAppliedRef = useRef(false);
  const publishedRef = useRef(false);

  const applyUser = useCallback(
    (user, token) => {
      if (hasAppliedRef.current) {
        return;
      }
      hasAppliedRef.current = true;

      if (!token || !user || user.invalidToken) {
        if (isMobile || isEnclosed) {
          window.top.postMessage(EXPIRED_JWT_MESSAGE, '*');
          setSessionNotice(
            isMobile ? MOBILE_SESSION_EXPIRED_MESSAGE : SESSION_EXPIRED_MESSAGE
          );
          setIsReady(true);
          return;
        }

        if (isFunction(invalidationFn)) {
          invalidationFn();
          return;
        }

        setAuthError(AUTH_FAILED_MESSAGE);
        setIsReady(true);
        return;
      }

      dispatch(setUser({ user, token }));
      dispatch(setMasterDataForLocalUser({}));
      dispatch(setMasterDataForSwipeclockUser({}));
      dispatch(setMetricsData({}));
      setIsReady(true);
    },
    [dispatch, invalidationFn, isEnclosed, isMobile]
  );

  const authenticateLocally = useCallback(() => {
    const { user, token } = authenticate();
    applyUser(user, token);
  }, [applyUser]);

  useEffect(() => {
    if (isMobile) {
      authenticateLocally();
      return undefined;
    }

    const unsubscribe = subscribe(
      'onSwipeclockUserRecieved',
      ({ user } = {}) => {
        unsubscribe();
        applyUser(user, user?.token);
      }
    );

    if (!publishedRef.current) {
      publishedRef.current = true;
      publish(
        name,
        'onSwipeclockUserRequested',
        { name },
        { errorIfNoParentListening: true }
      ).catch(() => {
        unsubscribe();
        authenticateLocally();
      });
    }

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reportExpiredSession = useCallback(() => {
    if (!isMobile) {
      return;
    }

    setSessionNotice(MOBILE_SESSION_EXPIRED_MESSAGE);
  }, [isMobile]);

  const contextValue = useMemo(
    () => ({ authError, sessionNotice, reportExpiredSession }),
    [authError, sessionNotice, reportExpiredSession]
  );

  if (!isReady) {
    return null;
  }

  return (
    <BootstrapAuthContext.Provider value={contextValue}>
      {children}
    </BootstrapAuthContext.Provider>
  );
}

WebClockBootstrap.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  invalidationFn: PropTypes.func,
};

export default function BootstrapGate({ children, name, invalidationFn }) {
  return (
    <WebClockBootstrap name={name} invalidationFn={invalidationFn}>
      {children}
    </WebClockBootstrap>
  );
}

BootstrapGate.propTypes = {
  children: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  invalidationFn: PropTypes.func,
};
